import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCompanies, createCompany, deleteCompany, seedData } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Building2, Users, Trash2, ChevronRight, LogOut, Shield } from 'lucide-react';

// Logo ICL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a3de8ce8-9ebc-47a8-b532-221c9e0f1005/artifacts/0dlj7jep_LOGO%20ICL.png";

const CompaniesPage = ({ onSelectCompany, isAdmin, onShowAdmin }) => {
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cif: '',
    sector: '',
    employees_count: ''
  });

  useEffect(() => {
    loadCompanies();
    // Seed data on first load
    seedData().catch(() => {});
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createCompany({
        ...formData,
        employees_count: formData.employees_count ? parseInt(formData.employees_count) : null
      });
      setFormData({ name: '', cif: '', sector: '', employees_count: '' });
      setDialogOpen(false);
      loadCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;
    try {
      await deleteCompany(companyToDelete.id);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      loadCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const confirmDelete = (company, e) => {
    e.stopPropagation();
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ICL Logo" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight">Cumplimiento Laboral</span>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onShowAdmin}
                data-testid="admin-panel-btn"
                className="border-sky-200 text-sky-700 hover:bg-sky-50"
              >
                <Shield className="w-4 h-4 mr-2" />
                Panel Admin
              </Button>
            )}
            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-600 hover:text-slate-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Mis empresas</h1>
            <p className="text-slate-600">Selecciona una empresa para gestionar su cumplimiento</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-company-btn" className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Nueva empresa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Añadir empresa</DialogTitle>
                <DialogDescription>
                  Introduce los datos de la nueva empresa
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la empresa *</Label>
                  <Input
                    id="name"
                    data-testid="company-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Acme S.L."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cif">CIF</Label>
                  <Input
                    id="cif"
                    data-testid="company-cif-input"
                    value={formData.cif}
                    onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                    placeholder="B12345678"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sector">Sector</Label>
                  <Input
                    id="sector"
                    data-testid="company-sector-input"
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    placeholder="Tecnología"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employees">Número de empleados</Label>
                  <Input
                    id="employees"
                    data-testid="company-employees-input"
                    type="number"
                    value={formData.employees_count}
                    onChange={(e) => setFormData({ ...formData, employees_count: e.target.value })}
                    placeholder="50"
                  />
                </div>
                <Button type="submit" data-testid="save-company-btn" className="w-full bg-slate-900 hover:bg-slate-800">
                  Guardar empresa
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : companies.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-12 h-12 text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay empresas</h3>
              <p className="text-slate-600 mb-6 text-center max-w-md">
                Añade tu primera empresa para comenzar a gestionar el cumplimiento normativo laboral
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Añadir empresa
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company, index) => (
              <Card 
                key={company.id} 
                className="border-slate-200 cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => onSelectCompany(company)}
                data-testid={`company-card-${company.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{company.name}</CardTitle>
                        {company.cif && (
                          <CardDescription className="mono text-xs">{company.cif}</CardDescription>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => confirmDelete(company, e)}
                      data-testid={`delete-company-${company.id}`}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      {company.sector && (
                        <span className="text-slate-600">{company.sector}</span>
                      )}
                      {company.employees_count && (
                        <span className="flex items-center gap-1 text-slate-500">
                          <Users className="w-4 h-4" />
                          {company.employees_count}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará "{companyToDelete?.name}" y todos sus datos de cumplimiento asociados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              data-testid="confirm-delete-company"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompaniesPage;
